import React, { useEffect, useRef } from "react";
import {
    Accordion,
    Box,
    Button,
    Text,
    List,
    Flex,
    rem,
    MantineTheme,
} from "@mantine/core";
import useAnalytics from "../hooks/useAnalytics.js";
import { LuSmartphone, LuLock } from "react-icons/lu";
import {
    getAccordionTileIconStyles,
    getAccordionTileStyles,
} from "./accordionTileStyles.js";

const DEFAULT_CSML_BASE_URL = "https://mylife.caresource.com/login";
const CSML_UTM_PARAMS = "utm_source=miv&utm_medium=cta&utm_campaign=csml_activation";

// NOTE: Do NOT append member identifiers or any PII/PHI to this link.
// Safely appends UTM params whether or not the base URL already has a
// query string.
function buildActivationUrl(baseUrl: string): string {
    const separator = baseUrl.includes("?") ? "&" : "?";
    return `${baseUrl}${separator}${CSML_UTM_PARAMS}`;
}

const CTA_DETAILS = {
    collapsedTitle: "Create Your {{PortalName}} Account",
    collapsedLine: "24/7/365 ID Card | Check Benefits | Search For Providers | See Health Alerts",
    expandedBody:
        "{{PortalName}} is a secured digital tool that gives you access to your health plan " +
        "information in one place. You can:",
    toolsDescription: [
        "View or print your digital ID card", 
        "Review your plan benefits and coverage", 
        "See your rewards and how to use them",
        "Manage care for your whole family",
        "Access claims and important documents",
        "See important health alerts",
        "Search for providers by location, specialty, or language",
    ],
    expandedHighlight: "It's fast and easy to create your account now!",
    ctaLabel: "Create Your Account",
};

// Substitutes the {{PortalName}} token in copy with the actual resolved product market.
function formatCopy(text: string, productMarket: string): string {
    return text.replaceAll("{{PortalName}}", productMarket);
}

interface CSMLCTAProps {
    productMarket?: string;
    activationUrl?: string;
}

// Shared responsive text sizing: small on mobile, scales up at the `sm`
// breakpoint and above. Used across all body copy in this widget.
function responsiveTextSize(theme: MantineTheme) {
    return {
        [`@media (min-width: ${theme.breakpoints.sm})`]: {
            fontSize: theme.fontSizes.sm,
        },
    };
}

export default function CSMLCTA(props: CSMLCTAProps) {
    const { productMarket = "CareSource MyLife", activationUrl = DEFAULT_CSML_BASE_URL } = props;
    const csmlActivationHref = buildActivationUrl(activationUrl);
    const { trackEvent, AnalyticsEvents } = useAnalytics();
    const itemRef = useRef<HTMLDivElement>(null);
    const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const scrollFrameRef = useRef<number | null>(null);

    // Ensure any pending scroll timers are cancelled if the component
    // unmounts before they fire (e.g. user navigates away mid-animation).
    useEffect(() => {
        return () => {
            if (scrollFrameRef.current !== null) {
                cancelAnimationFrame(scrollFrameRef.current);
            }
            if (scrollTimeoutRef.current !== null) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, []);

    function handleChange(value: string | null) {
        // Cancel any previously scheduled scroll so rapid toggling doesn't
        // cause a stale, delayed scroll to fire after the widget has
        // already been collapsed/re-expanded.
        if (scrollFrameRef.current !== null) {
            cancelAnimationFrame(scrollFrameRef.current);
            scrollFrameRef.current = null;
        }
        if (scrollTimeoutRef.current !== null) {
            clearTimeout(scrollTimeoutRef.current);
            scrollTimeoutRef.current = null;
        }

        // Only fire when opening, not on collapse, to avoid noisy duplicate events
        if (value) {
            trackEvent(AnalyticsEvents.CSML_WIDGET_EXPANDED);

            // Scroll the widget into view once it expands, after giving the
            // collapse animation a moment to start so the final scroll target
            // (post-expansion height) is accounted for.
            scrollFrameRef.current = requestAnimationFrame(() => {
                scrollTimeoutRef.current = setTimeout(() => {
                    itemRef.current?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                    });
                    scrollTimeoutRef.current = null;
                }, 150);
                scrollFrameRef.current = null;
            });
        }
    }

    function handleCtaClick() {
        trackEvent(AnalyticsEvents.CSML_CTA_CLICKED, { referrer: "miv" });
    }

    return (
        <Accordion
            variant="contained"
            mt={"xl"}
            onChange={handleChange}
            data-testid="csml-cta-widget"
            styles={getAccordionTileStyles}
        >
            <Accordion.Item value="csml-cta" ref={itemRef}>
                <Accordion.Control data-testid="csml-cta-toggle">
                    <Flex align={"center"} gap={"sm"}>
                        <Box sx={getAccordionTileIconStyles}>
                            <LuSmartphone color="white" />
                        </Box>
                        <Box>
                            <Text
                                fw={700}
                                fz="xs"
                                sx={responsiveTextSize}
                            >
                                {formatCopy(CTA_DETAILS.collapsedTitle, productMarket)}
                            </Text>
                            <Text
                                fz="xs"
                                color={"cs-gray.600"}
                                sx={responsiveTextSize}
                            >
                                {formatCopy(CTA_DETAILS.collapsedLine, productMarket)}
                            </Text>
                        </Box>
                    </Flex>
                </Accordion.Control>
                <Accordion.Panel data-testid="csml-cta-expanded">
                    <Text fz="xs" mb={"md"} sx={responsiveTextSize}>
                        {formatCopy(CTA_DETAILS.expandedBody, productMarket)}
                    </Text>
                    <List
                        mb={rem(16)}
                        ml={rem(16)}
                        mr={rem(16)}
                        styles={(theme) => ({
                            itemWrapper: {
                                fontSize: theme.fontSizes.xs,

                                [`@media (min-width: ${theme.breakpoints.sm})`]:
                                    {
                                        fontSize: theme.fontSizes.sm,
                                    },
                            },
                            item: {
                                "&::marker": {
                                    color: theme.colors[theme.primaryColor][5],
                                },
                            },
                            itemIcon: {
                                color: theme.colors[theme.primaryColor][5],
                            },
                        })}
                    >
                        {CTA_DETAILS.toolsDescription.map((tool, index) => (
                            <List.Item key={index}>{tool}</List.Item>
                        ))}
                    </List>
                    <Text
                        fz="xs"
                        fw={700}
                        mb={"md"}
                        sx={(theme) => ({
                            color: theme.colors[theme.primaryColor][5],
                            ...responsiveTextSize(theme),
                        })}
                    >
                        {CTA_DETAILS.expandedHighlight}
                    </Text>
                    <Button
                        component="a"
                        href={csmlActivationHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        fullWidth
                        onClick={handleCtaClick}
                        data-testid="csml-cta-button"
                    >
                        <Flex align={"center"} gap={"sm"}>
                            <LuLock />
                            {CTA_DETAILS.ctaLabel}
                        </Flex>
                    </Button>
                </Accordion.Panel>
            </Accordion.Item>
        </Accordion>
    );
}
